import type { ReportRow } from '@/api/keywordMetrics';
import {
  EMPTY_RAW_METRICS,
  accumulateInto,
  campaignLookupKey,
  rowInCampaignScope,
  rowToRaw,
  type MetricPolarity,
  type RawMetricTotals,
  type WorkspaceMetricColumnDef,
} from '@/utils/workspaceAdProductMetrics';
import {
  deriveTargetingTypeMetrics,
  normalizeMatchType,
  type CampaignTargetingIndex,
  type TargetingTypeDerivedMetrics,
  type TargetingTypeSummaryMetrics,
} from '@/utils/workspaceTargetingTypeMetrics';

export const MATCH_TYPE_IDS = [
  'broad',
  'exact',
  'phrase',
  'auto',
  'category',
  'audience',
  'asin',
  'theme',
] as const;

export type MatchTypeId = (typeof MATCH_TYPE_IDS)[number];

export const MATCH_TYPE_ROWS: Array<{ id: MatchTypeId; label: string }> = [
  { id: 'broad', label: 'BROAD' },
  { id: 'exact', label: 'EXACT' },
  { id: 'phrase', label: 'PHRASE' },
  { id: 'auto', label: 'AUTO' },
  { id: 'category', label: 'CATEGORY' },
  { id: 'audience', label: 'AUDIENCE' },
  { id: 'asin', label: 'ASIN' },
  { id: 'theme', label: 'THEME' },
];

export type MatchTypeDerivedMetrics = TargetingTypeDerivedMetrics;

export const EMPTY_MATCH_TYPE_METRICS: MatchTypeDerivedMetrics = {
  ...EMPTY_RAW_METRICS,
  ctr: 0,
  cpc: 0,
  cr: 0,
  acos: 0,
  roas: 0,
  asp: 0,
};

export type MatchTypeMetricColumnDef = {
  key: keyof MatchTypeDerivedMetrics;
  label: string;
  polarity: MetricPolarity;
  format: WorkspaceMetricColumnDef['format'];
};

/** Same column order as Targeting Type Breakdown */
export const MATCH_TYPE_METRIC_COLUMNS: MatchTypeMetricColumnDef[] = [
  { key: 'cost', label: 'Spend', polarity: 'lower_is_better', format: 'currency' },
  { key: 'cpc', label: 'CPC', polarity: 'lower_is_better', format: 'currency' },
  { key: 'ctr', label: 'CTR', polarity: 'higher_is_better', format: 'percent' },
  { key: 'cr', label: 'CR%', polarity: 'higher_is_better', format: 'percent' },
  { key: 'purchases30d', label: 'Orders', polarity: 'higher_is_better', format: 'int' },
  { key: 'impressions', label: 'Impressions', polarity: 'higher_is_better', format: 'int' },
  { key: 'clicks', label: 'Clicks', polarity: 'higher_is_better', format: 'int' },
  { key: 'sales30d', label: 'Sales', polarity: 'higher_is_better', format: 'currency' },
  { key: 'acos', label: 'ACoS', polarity: 'lower_is_better', format: 'percent' },
  { key: 'roas', label: 'ROAS', polarity: 'higher_is_better', format: 'ratio' },
  { key: 'asp', label: 'ASP', polarity: 'higher_is_better', format: 'currency' },
];

export const deriveMatchTypeMetrics = deriveTargetingTypeMetrics;

export function workspaceKeywordMatchLabel(matchType: string): string {
  const token = canonicalAutoGroupId(matchType);
  if (token === 'CLOSE_MATCH') return 'Close match';
  if (token === 'LOOSE_MATCH') return 'Loose match';
  if (token === 'SUBSTITUTES') return 'Substitutes';
  if (token === 'COMPLEMENTS') return 'Complements';
  return matchType;
}

export function canonicalAutoGroupId(matchType?: string, targetingText?: string): string {
  return autoGroupToken(normalizeMatchType(matchType)) || autoGroupToken(normalizeMatchType(targetingText));
}

function autoGroupToken(token: string): string {
  if (
    token === 'CLOSE_MATCH' ||
    token === 'QUERY_HIGH_REL_MATCHES' ||
    token === 'KEYWORDS_CLOSE_MATCH' ||
    token === 'SEARCH_CLOSE_MATCH'
  ) {
    return 'CLOSE_MATCH';
  }
  if (
    token === 'LOOSE_MATCH' ||
    token === 'QUERY_BROAD_REL_MATCHES' ||
    token === 'KEYWORDS_LOOSE_MATCH' ||
    token === 'SEARCH_LOOSE_MATCH'
  ) {
    return 'LOOSE_MATCH';
  }
  if (token === 'SUBSTITUTES' || token === 'ASIN_SUBSTITUTE_RELATED' || token === 'PRODUCT_SUBSTITUTES') {
    return 'SUBSTITUTES';
  }
  if (token === 'COMPLEMENTS' || token === 'ASIN_ACCESSORY_RELATED' || token === 'PRODUCT_COMPLEMENTS') {
    return 'COMPLEMENTS';
  }
  return '';
}

const AUTO_MATCH_TYPES = new Set([
  'CLOSE_MATCH',
  'LOOSE_MATCH',
  'SUBSTITUTES',
  'COMPLEMENTS',
  'QUERY_HIGH_REL_MATCHES',
  'QUERY_BROAD_REL_MATCHES',
  'ASIN_SUBSTITUTE_RELATED',
  'ASIN_ACCESSORY_RELATED',
  'KEYWORDS_CLOSE_MATCH',
  'KEYWORDS_LOOSE_MATCH',
  'PRODUCT_SUBSTITUTES',
  'PRODUCT_COMPLEMENTS',
  'SEARCH_CLOSE_MATCH',
  'SEARCH_LOOSE_MATCH',
]);

const KEYWORD_MATCH_TO_ID: Record<string, MatchTypeId> = {
  BROAD: 'broad',
  EXACT: 'exact',
  PHRASE: 'phrase',
};

const CATEGORY_MATCH_TYPES = new Set([
  'ASIN_CATEGORY_SAME_AS',
  'ASIN_BRAND_SAME_AS',
]);

function signalsTheme(matchType: string, targetingText: string): boolean {
  if (
    matchType === 'THEME' ||
    matchType.startsWith('KEYWORDS_RELATED_TO_') ||
    matchType === 'BRAND' ||
    matchType === 'GIFT' ||
    matchType === 'CATEGORY'
  ) {
    return true;
  }
  return matchType.includes('THEME') || /theme/i.test(targetingText);
}

function signalsAudience(targetingText: string, tactic: string | null): boolean {
  const text = targetingText.toLowerCase();
  if (text.includes('audience=')) return true;
  return tactic === 'T00030' && targetingText.trim() === '';
}

function signalsViews(targetingText: string): boolean {
  return targetingText.toLowerCase().includes('views=');
}

function signalsKeywordGroup(targetingText: string): boolean {
  return targetingText.toLowerCase().includes('keyword-group=');
}

function signalsAuto(matchType: string, targetingType: string | null): boolean {
  if (targetingType === 'AUTO') return true;
  return AUTO_MATCH_TYPES.has(matchType);
}

function signalsCategory(matchType: string, targetingText: string): boolean {
  if (CATEGORY_MATCH_TYPES.has(matchType)) return true;
  if (/ASIN_CATEGORY/i.test(matchType)) return true;
  if (/ASIN_(BRAND|PRICE|REVIEW)_/i.test(matchType)) return true;
  return targetingText.toLowerCase().includes('category=');
}

function signalsAsin(matchType: string, targetingText: string): boolean {
  if (AUTO_MATCH_TYPES.has(matchType)) return false;
  if (signalsCategory(matchType, targetingText)) return false;
  if (
    matchType === 'ASIN_SAME_AS' ||
    matchType === 'ASIN_EXPANDED_FROM' ||
    (matchType.startsWith('ASIN_') && matchType.length > 0)
  ) {
    return true;
  }
  const text = targetingText.toLowerCase();
  return text.includes('asin=') || text.includes('asin_');
}

/**
 * Classify a report row into exactly one match-type bucket.
 * Priority: Theme → audience= → views= remarketing → keyword-group → Auto
 * → Category → ASIN → BROAD/EXACT/PHRASE. Views must run before category= so
 * views=(category="…") stays AUDIENCE. Keyword-group maps to Category and runs before Auto.
 * Returns null when the row cannot be attributed (unknown keyword matchType stays unclassified).
 */
export function classifyMatchType(
  row: ReportRow,
  campaignIndex: CampaignTargetingIndex
): MatchTypeId | null {
  const campaignId = row.campaignId ? String(row.campaignId) : '';
  const adProduct = row.adProduct ? String(row.adProduct) : 'sponsored_products';
  if (!campaignId) return null;

  const meta = campaignIndex.get(campaignLookupKey(adProduct, campaignId));
  const matchType = normalizeMatchType(row.matchType);
  const targetingText = row.targetingText ? String(row.targetingText) : '';
  const targetingType = meta?.targetingType ?? null;
  const tactic = meta?.tactic ?? null;
  const isTargetRow = Boolean(row.targetId) || Boolean(targetingText);

  if (signalsTheme(matchType, targetingText)) {
    return 'theme';
  }

  if (signalsAudience(targetingText, tactic)) {
    return 'audience';
  }

  if (signalsViews(targetingText)) {
    return 'audience';
  }

  // Keyword groups are Category. Before Auto so AUTO campaigns cannot swallow them.
  if (signalsKeywordGroup(targetingText)) {
    return 'category';
  }

  if (signalsAuto(matchType, targetingType)) {
    return 'auto';
  }

  if (signalsCategory(matchType, targetingText)) {
    return 'category';
  }

  if (signalsAsin(matchType, targetingText)) {
    return 'asin';
  }

  // SD target rows without audience/category/asin text → default product context to ASIN
  if (isTargetRow && adProduct === 'sponsored_display') {
    return 'asin';
  }

  const keywordBucket = KEYWORD_MATCH_TO_ID[matchType];
  if (keywordBucket) {
    return keywordBucket;
  }

  return null;
}

function finalizeMatchTypeMap(
  map: Map<string, RawMetricTotals>
): Map<MatchTypeId, MatchTypeDerivedMetrics> {
  const out = new Map<MatchTypeId, MatchTypeDerivedMetrics>();
  for (const id of MATCH_TYPE_IDS) {
    const raw = map.get(id) ?? { ...EMPTY_RAW_METRICS };
    out.set(id, deriveMatchTypeMetrics(raw));
  }
  return out;
}

export function aggregateMatchTypeMetrics(
  keywordRows: ReportRow[],
  targetRows: ReportRow[],
  campaignIndex: CampaignTargetingIndex,
  scopedCampaignLookupKeys: Set<string> | null
): Map<MatchTypeId, MatchTypeDerivedMetrics> {
  const rawByType = new Map<string, RawMetricTotals>();
  for (const id of MATCH_TYPE_IDS) {
    rawByType.set(id, { ...EMPTY_RAW_METRICS });
  }

  const ingest = (row: ReportRow, defaultAdProduct: string) => {
    const campaignId = row.campaignId ? String(row.campaignId) : '';
    const adProduct = row.adProduct ? String(row.adProduct) : defaultAdProduct;
    if (!campaignId || !rowInCampaignScope(adProduct, campaignId, scopedCampaignLookupKeys)) {
      return;
    }
    const bucket = classifyMatchType({ ...row, adProduct }, campaignIndex);
    if (!bucket) return;
    accumulateInto(rawByType, bucket, rowToRaw(row));
  };

  for (const row of keywordRows) {
    ingest(row, 'sponsored_products');
  }
  for (const row of targetRows) {
    ingest(row, row.adProduct ? String(row.adProduct) : 'sponsored_display');
  }

  return finalizeMatchTypeMap(rawByType);
}

function coerceMatchMetrics(
  value: Partial<MatchTypeDerivedMetrics> | null | undefined
): MatchTypeDerivedMetrics {
  if (!value) return { ...EMPTY_MATCH_TYPE_METRICS };
  return deriveMatchTypeMetrics({
    impressions: Number(value.impressions) || 0,
    clicks: Number(value.clicks) || 0,
    cost: Number(value.cost) || 0,
    purchases30d: Number(value.purchases30d) || 0,
    sales30d: Number(value.sales30d) || 0,
  });
}

export type MatchTypeSummaryPayload = {
  buckets?: Array<{
    id: string;
    label: string;
    current: TargetingTypeSummaryMetrics;
    previous: TargetingTypeSummaryMetrics | null;
  }>;
  unclassified?: {
    current?: Partial<TargetingTypeSummaryMetrics> | null;
    previous?: Partial<TargetingTypeSummaryMetrics> | null;
  };
  totals?: {
    current?: TargetingTypeSummaryMetrics | null;
    previous?: TargetingTypeSummaryMetrics | null;
  };
};

/** Map backend match-type summary payload into Workspace table maps. */
export function matchTypeMapsFromSummary(
  data: MatchTypeSummaryPayload | null | undefined
): {
  current: Map<MatchTypeId, MatchTypeDerivedMetrics>;
  previous: Map<MatchTypeId, MatchTypeDerivedMetrics>;
  currentTotals: MatchTypeDerivedMetrics;
  previousTotals: MatchTypeDerivedMetrics;
} {
  const current = new Map<MatchTypeId, MatchTypeDerivedMetrics>();
  const previous = new Map<MatchTypeId, MatchTypeDerivedMetrics>();
  for (const id of MATCH_TYPE_IDS) {
    current.set(id, { ...EMPTY_MATCH_TYPE_METRICS });
    previous.set(id, { ...EMPTY_MATCH_TYPE_METRICS });
  }
  for (const bucket of data?.buckets ?? []) {
    if (!MATCH_TYPE_IDS.includes(bucket.id as MatchTypeId)) continue;
    const id = bucket.id as MatchTypeId;
    current.set(id, coerceMatchMetrics(bucket.current));
    previous.set(id, coerceMatchMetrics(bucket.previous));
  }
  const currentTotals = data?.totals?.current
    ? coerceMatchMetrics(data.totals.current)
    : coerceMatchMetrics(
        [...current.values(), coerceMatchMetrics(data?.unclassified?.current)].reduce(
          (acc, row) => ({
            impressions: acc.impressions + row.impressions,
            clicks: acc.clicks + row.clicks,
            cost: acc.cost + row.cost,
            purchases30d: acc.purchases30d + row.purchases30d,
            sales30d: acc.sales30d + row.sales30d,
          }),
          { impressions: 0, clicks: 0, cost: 0, purchases30d: 0, sales30d: 0 }
        )
      );
  const previousTotals = data?.totals?.previous
    ? coerceMatchMetrics(data.totals.previous)
    : coerceMatchMetrics(
        [...previous.values(), coerceMatchMetrics(data?.unclassified?.previous)].reduce(
          (acc, row) => ({
            impressions: acc.impressions + row.impressions,
            clicks: acc.clicks + row.clicks,
            cost: acc.cost + row.cost,
            purchases30d: acc.purchases30d + row.purchases30d,
            sales30d: acc.sales30d + row.sales30d,
          }),
          { impressions: 0, clicks: 0, cost: 0, purchases30d: 0, sales30d: 0 }
        )
      );
  return { current, previous, currentTotals, previousTotals };
}
